import { useQuery } from '@tanstack/react-query';
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { buildTestCounts } from '@/features/dashboard/components/dashboardStatusDataHelpers';
import { useDashboardDateRange, type DashboardRange } from '@/features/dashboard/hooks';
import { queryTestExecutions, queryTestExecutionStatusSummary } from '@/features/executions/data';
import { buildChartDates, buildLineChartSeries } from '@/features/executions/utils/chartData';
import { buildStackedExecutionChartData } from '@/features/executions/utils/stackedExecutionChart';
import { buildMetricValueItems } from '@/features/metrics/components/metricValueItems';
import {
  buildStatusDeltaMap,
  buildStatusMetricCards,
  mapStatusOptionsToMetricOptions,
  resolveMetricTone,
} from '@/features/metrics/components/statusMetricCards';
import { useMetricsQuery } from '@/features/metrics/hooks/metrics';
import { buildThresholdGuideLines } from '@/features/metrics/lib/chartThresholdGuides';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter, useProjectMetricThresholds } from '@/features/projects';
import {
  buildSelectedDayRangeFilters,
  resolveChartDaySelection,
} from '@/features/shared/hooks/detailDateHelpers';
import {
  resolveFrequencyPerDay,
  resolveTimestamp,
} from '@/features/shared/metrics/detailExecutionMetrics';
import { testExecutionSummaryStatusOptions } from '@/shared/constants/statusOptions';
import { queryKeys, useAccessToken, usePersistedStringArray, useTagOptions } from '@/shared/hooks';
import {
  buildDateRangeFilters,
  buildEqFilterValue,
  buildInFilter,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import type { TestDetailDto, TestExecutionStatusSummaryDto } from '@flowqa/shared';

type TestDetailPageViewModelParams = {
  test: TestDetailDto | null | undefined;
};

type TestChartTab = 'status' | 'pass-rate' | 'frequency' | 'duration';

export type TestDetailPageViewModel = {
  title: string;
  testId: string;
  chartTab: TestChartTab;
  setChartTab: Dispatch<SetStateAction<TestChartTab>>;
  suppressChartAnimation: boolean;
  range: DashboardRange;
  setRange: Dispatch<SetStateAction<DashboardRange>>;
  selectedStartDay: string | null;
  setSelectedStartDay: Dispatch<SetStateAction<string | null>>;
  selectedEndDay: string | null;
  setSelectedEndDay: Dispatch<SetStateAction<string | null>>;
  isAllMode: boolean;
  pickerStartDay: string | null;
  pickerEndDay: string | null;
  handleStartDayChange: (nextDay: string | null) => void;
  handleEndDayChange: (nextDay: string | null) => void;
  testMetricCards: ReturnType<typeof buildStatusMetricCards>;
  metricValueItems: ReturnType<typeof buildMetricValueItems>;
  chartHeight: number;
  showChartDateClear: boolean;
  handleChartDateClear: () => void;
  stackedChartData: ReturnType<typeof buildStackedExecutionChartData>;
  passRateChartData: ReturnType<typeof buildLineChartSeries> | [] | null;
  frequencyChartData: ReturnType<typeof buildLineChartSeries> | [] | null;
  durationChartData: ReturnType<typeof buildLineChartSeries> | [] | null;
  passRateGuideLines: ReturnType<typeof buildThresholdGuideLines>;
  frequencyGuideLines: ReturnType<typeof buildThresholdGuideLines>;
  durationGuideLines: ReturnType<typeof buildThresholdGuideLines>;
  handleChartSelect: (dataIndex: number) => void;
  showLineChartPoints: boolean;
  selectedTags: string[];
  tagOptions: Array<{ value: string; label: string }>;
  tagOptionsLoading: boolean;
  handleTagsChange: (next: string[]) => void;
  testExecutionFilters: ReturnType<typeof mergeFilters>;
};

const useTestDetailPageViewModelInternal = ({
  test,
}: TestDetailPageViewModelParams): TestDetailPageViewModel => {
  const title = test?.name ?? '';
  const testId = test?.id ?? '';
  const [chartTab, setChartTab] = useState<'status' | 'pass-rate' | 'frequency' | 'duration'>(
    'status',
  );
  const [suppressChartAnimation, setSuppressChartAnimation] = useState(false);
  const [range, setRange] = useState<DashboardRange>('7d');
  const [selectedStartDay, setSelectedStartDay] = useState<string | null>(null);
  const [selectedEndDay, setSelectedEndDay] = useState<string | null>(null);
  const [selectedTags, setSelectedTags] = usePersistedStringArray('tag-filter:test-detail');
  const { rangeStartIso, rangeEndIso, previousRangeStartIso, previousRangeEndIso } =
    useDashboardDateRange(range);
  const accessToken = useAccessToken();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const { tagOptions, loading: tagOptionsLoading } = useTagOptions({
    projectId: selectedProjectId,
    enabled: Boolean(accessToken),
  });
  const { thresholds } = useProjectMetricThresholds(selectedProjectId);
  const isAllMode = range === 'all';
  const queryEnabled = Boolean(accessToken && test && selectedProjectId);

  const dateRangeFilters = useMemo(
    () => buildDateRangeFilters('timestamp', rangeStartIso, rangeEndIso),
    [rangeEndIso, rangeStartIso],
  );
  const previousDateRangeFilters = useMemo(
    () => buildDateRangeFilters('timestamp', previousRangeStartIso, previousRangeEndIso),
    [previousRangeEndIso, previousRangeStartIso],
  );
  const baseTestFilters = useMemo(() => buildEqFilterValue('testId', testId), [testId]);
  const selectedDayFilters = useMemo(() => {
    return buildSelectedDayRangeFilters('timestamp', selectedStartDay, selectedEndDay);
  }, [selectedEndDay, selectedStartDay]);
  const tagFilters = useMemo(() => buildInFilter('tags', selectedTags), [selectedTags]);
  const testExecutionFilters = useMemo(
    () =>
      mergeFilters(
        baseTestFilters,
        selectedDayFilters,
        !isAllMode ? dateRangeFilters : undefined,
        tagFilters,
      ),
    [baseTestFilters, dateRangeFilters, isAllMode, selectedDayFilters, tagFilters],
  );
  const hasCustomDates = Boolean(selectedStartDay ?? selectedEndDay);
  const metricsRange = hasCustomDates ? 'all' : range;
  const metricsQuery = useMetricsQuery({
    range: metricsRange,
    statuses: [],
    scope: 'test',
    projectId: selectedProjectId,
    entityId: testId,
    name: title,
    startDay: hasCustomDates ? selectedStartDay : null,
    endDay: hasCustomDates ? selectedEndDay : null,
    enabled: queryEnabled,
  });
  const previousStartDay = previousRangeStartIso?.slice(0, 10) ?? null;
  const previousEndDay = previousRangeEndIso?.slice(0, 10) ?? null;
  const showMetricDelta = Boolean(
    !isAllMode && !hasCustomDates && previousStartDay && previousEndDay,
  );
  const previousMetricsQuery = useMetricsQuery({
    range: 'all',
    statuses: [],
    scope: 'test',
    projectId: selectedProjectId,
    entityId: testId,
    name: title,
    startDay: previousStartDay,
    endDay: previousEndDay,
    enabled: queryEnabled && showMetricDelta,
  });
  const earliestExecutionQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'test-executions/earliest',
      testId,
      selectedProjectId ?? null,
      selectedOrganisationId ?? null,
    ),
    enabled: queryEnabled,
    queryFn: async () =>
      await queryTestExecutions(
        selectedProjectId,
        selectedOrganisationId ?? undefined,
        {
          page: 1,
          pageSize: 1,
          sorts: [{ field: 'timestamp', direction: 'asc' }],
          filters: baseTestFilters,
        },
        accessToken,
      ),
  });
  const latestExecutionQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'test-executions/latest',
      testId,
      selectedProjectId ?? null,
      selectedOrganisationId ?? null,
    ),
    enabled: queryEnabled,
    queryFn: async () =>
      await queryTestExecutions(
        selectedProjectId,
        selectedOrganisationId ?? undefined,
        {
          page: 1,
          pageSize: 1,
          sorts: [{ field: 'timestamp', direction: 'desc' }],
          filters: baseTestFilters,
        },
        accessToken,
      ),
  });
  const metricsStartDay = useMemo(() => {
    const items = earliestExecutionQuery.data ? earliestExecutionQuery.data.items : [];
    if (items.length === 0) return null;
    const dateValue = resolveTimestamp(items[0].timestamp);
    return dateValue ? dateValue.toISOString().slice(0, 10) : null;
  }, [earliestExecutionQuery.data]);
  const metricsEndDay = useMemo(() => {
    const items = latestExecutionQuery.data ? latestExecutionQuery.data.items : [];
    if (items.length === 0) return null;
    const dateValue = resolveTimestamp(items[0].timestamp);
    return dateValue ? dateValue.toISOString().slice(0, 10) : null;
  }, [latestExecutionQuery.data]);
  const pickerStartDay = useMemo(() => {
    if (selectedStartDay) return selectedStartDay;
    if (!isAllMode) {
      return rangeStartIso ? rangeStartIso.slice(0, 10) : null;
    }
    return metricsStartDay;
  }, [isAllMode, metricsStartDay, rangeStartIso, selectedStartDay]);
  const pickerEndDay = useMemo(() => {
    if (selectedEndDay) return selectedEndDay;
    if (!isAllMode) {
      return rangeEndIso ? rangeEndIso.slice(0, 10) : null;
    }
    return metricsEndDay;
  }, [isAllMode, metricsEndDay, rangeEndIso, selectedEndDay]);
  const handleStartDayChange = useMemo(
    () => (nextDay: string | null) => {
      setSelectedStartDay(nextDay);
      if (!nextDay) {
        setSelectedEndDay(null);
        return;
      }
      if (!selectedEndDay) {
        setSelectedEndDay(pickerEndDay ?? nextDay);
      }
    },
    [pickerEndDay, selectedEndDay],
  );
  const handleEndDayChange = useMemo(
    () => (nextDay: string | null) => {
      setSelectedEndDay(nextDay);
      if (!nextDay) {
        setSelectedStartDay(null);
        return;
      }
      if (!selectedStartDay) {
        setSelectedStartDay(pickerStartDay ?? nextDay);
      }
    },
    [pickerStartDay, selectedStartDay],
  );
  const summaryInput = useMemo(
    () => ({
      page: 1,
      pageSize: 1,
      sorts: [],
      filters: testExecutionFilters,
    }),
    [testExecutionFilters],
  );
  const summaryFiltersKey = useMemo(
    () => JSON.stringify(summaryInput.filters),
    [summaryInput.filters],
  );
  const currentEarliestQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'test-executions/current-earliest',
      summaryFiltersKey,
      selectedProjectId ?? null,
      selectedOrganisationId ?? null,
    ),
    enabled: queryEnabled,
    queryFn: async () =>
      await queryTestExecutions(
        selectedProjectId,
        selectedOrganisationId ?? undefined,
        {
          page: 1,
          pageSize: 1,
          sorts: [{ field: 'timestamp', direction: 'asc' }],
          filters: testExecutionFilters,
        },
        accessToken,
      ),
  });
  const currentLatestQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'test-executions/current-latest',
      summaryFiltersKey,
      selectedProjectId ?? null,
      selectedOrganisationId ?? null,
    ),
    enabled: queryEnabled,
    queryFn: async () =>
      await queryTestExecutions(
        selectedProjectId,
        selectedOrganisationId ?? undefined,
        {
          page: 1,
          pageSize: 1,
          sorts: [{ field: 'timestamp', direction: 'desc' }],
          filters: testExecutionFilters,
        },
        accessToken,
      ),
  });
  const testSummaryQuery = useQuery<TestExecutionStatusSummaryDto>({
    queryKey: queryKeys.testExecutionSummary(
      selectedProjectId ?? null,
      selectedOrganisationId ?? null,
      summaryFiltersKey,
    ),
    enabled: queryEnabled,
    queryFn: async () =>
      await queryTestExecutionStatusSummary(
        selectedProjectId,
        selectedOrganisationId ?? undefined,
        summaryInput,
        accessToken,
      ),
  });
  const testSummary = testSummaryQuery.data ?? null;
  const showStatusDelta = Boolean(
    !isAllMode && !hasCustomDates && previousRangeStartIso && previousRangeEndIso,
  );
  const previousSummaryFilters = useMemo(
    () => mergeFilters(baseTestFilters, previousDateRangeFilters, tagFilters),
    [baseTestFilters, previousDateRangeFilters, tagFilters],
  );
  const previousSummaryInput = useMemo(
    () =>
      showStatusDelta
        ? {
            page: 1,
            pageSize: 1,
            sorts: [],
            filters: previousSummaryFilters,
          }
        : null,
    [previousSummaryFilters, showStatusDelta],
  );
  const previousSummaryFiltersKey = useMemo(
    () => (previousSummaryInput ? JSON.stringify(previousSummaryInput.filters) : 'latest'),
    [previousSummaryInput],
  );
  const previousEarliestQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'test-executions/previous-earliest',
      previousSummaryFiltersKey,
      selectedProjectId ?? null,
      selectedOrganisationId ?? null,
    ),
    enabled: Boolean(queryEnabled && previousSummaryInput),
    queryFn: async () =>
      await queryTestExecutions(
        selectedProjectId,
        selectedOrganisationId ?? undefined,
        {
          page: 1,
          pageSize: 1,
          sorts: [{ field: 'timestamp', direction: 'asc' }],
          filters: previousSummaryFilters,
        },
        accessToken,
      ),
  });
  const previousLatestQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'test-executions/previous-latest',
      previousSummaryFiltersKey,
      selectedProjectId ?? null,
      selectedOrganisationId ?? null,
    ),
    enabled: Boolean(queryEnabled && previousSummaryInput),
    queryFn: async () =>
      await queryTestExecutions(
        selectedProjectId,
        selectedOrganisationId ?? undefined,
        {
          page: 1,
          pageSize: 1,
          sorts: [{ field: 'timestamp', direction: 'desc' }],
          filters: previousSummaryFilters,
        },
        accessToken,
      ),
  });
  const previousTestSummaryQuery = useQuery<TestExecutionStatusSummaryDto>({
    queryKey: queryKeys.testExecutionSummary(
      selectedProjectId ?? null,
      selectedOrganisationId ?? null,
      previousSummaryFiltersKey,
    ),
    enabled: Boolean(queryEnabled && previousSummaryInput),
    queryFn: async () =>
      await queryTestExecutionStatusSummary(
        selectedProjectId,
        selectedOrganisationId ?? undefined,
        previousSummaryInput ?? { page: 1, pageSize: 1, sorts: [], filters: [] },
        accessToken,
      ),
  });
  const previousTestSummary = showStatusDelta ? (previousTestSummaryQuery.data ?? null) : null;
  const testCounts = useMemo(() => buildTestCounts(testSummary), [testSummary]);
  const previousTestCounts = useMemo(
    () => buildTestCounts(previousTestSummary),
    [previousTestSummary],
  );
  const testDeltas = useMemo(
    () =>
      showStatusDelta && previousTestSummary
        ? buildStatusDeltaMap(testCounts, previousTestCounts)
        : undefined,
    [previousTestCounts, previousTestSummary, showStatusDelta, testCounts],
  );
  const testOptions = useMemo(
    () => mapStatusOptionsToMetricOptions(testExecutionSummaryStatusOptions, resolveMetricTone),
    [],
  );
  const totalCount = useMemo(
    () =>
      Object.values(testCounts).reduce(
        (total, value) => total + (typeof value === 'number' ? value : 0),
        0,
      ),
    [testCounts],
  );
  const testMetricCards = useMemo(
    () =>
      buildStatusMetricCards({
        options: testOptions,
        counts: testCounts,
        deltas: testDeltas,
        total: totalCount,
        loading: testSummaryQuery.isLoading,
        showDelta: showStatusDelta,
      }),
    [showStatusDelta, testCounts, testDeltas, testOptions, testSummaryQuery.isLoading, totalCount],
  );
  const currentFrequency = useMemo(() => {
    const earliestItems = currentEarliestQuery.data ? currentEarliestQuery.data.items : [];
    const latestItems = currentLatestQuery.data ? currentLatestQuery.data.items : [];
    return resolveFrequencyPerDay(
      testSummary?.total ?? null,
      resolveTimestamp(earliestItems[0] ? earliestItems[0].timestamp : null),
      resolveTimestamp(latestItems[0] ? latestItems[0].timestamp : null),
    );
  }, [currentEarliestQuery.data, currentLatestQuery.data, testSummary?.total]);
  const previousFrequency = useMemo(
    () =>
      showMetricDelta
        ? resolveFrequencyPerDay(
            previousTestSummary?.total ?? null,
            resolveTimestamp(
              previousEarliestQuery.data?.items[0]
                ? previousEarliestQuery.data.items[0].timestamp
                : null,
            ),
            resolveTimestamp(
              previousLatestQuery.data?.items[0]
                ? previousLatestQuery.data.items[0].timestamp
                : null,
            ),
          )
        : null,
    [
      previousEarliestQuery.data,
      previousLatestQuery.data,
      previousTestSummary?.total,
      showMetricDelta,
    ],
  );
  const metricValueItems = useMemo(
    () =>
      buildMetricValueItems({
        metrics: metricsQuery.data ?? null,
        previousMetrics: showMetricDelta ? (previousMetricsQuery.data ?? null) : null,
        thresholds: thresholds.tests,
        frequencyOverride: currentFrequency,
        previousFrequencyOverride: previousFrequency,
        showDelta: showMetricDelta,
      }),
    [
      currentFrequency,
      metricsQuery.data,
      previousFrequency,
      previousMetricsQuery.data,
      showMetricDelta,
      thresholds.tests,
    ],
  );
  const chartHeight = 360;
  const chartDates = useMemo(() => {
    const labels = metricsQuery.data?.metricsRangeLabels ?? [];
    const dates = metricsQuery.data?.metricsRangeDates ?? [];
    return buildChartDates(labels, dates);
  }, [metricsQuery.data?.metricsRangeDates, metricsQuery.data?.metricsRangeLabels]);
  const lineChartLabels = metricsQuery.data?.metricsRangeLabels ?? [];
  const stackedChartData = useMemo(
    () => buildStackedExecutionChartData(metricsQuery.data),
    [metricsQuery.data],
  );
  const passRateChartData = useMemo(() => {
    const series = buildLineChartSeries(
      metricsQuery.data?.series.filteredPassRate,
      lineChartLabels,
      chartDates,
      'passRate',
    );
    if (series) return series;
    return metricsQuery.isFetched ? [] : null;
  }, [
    chartDates,
    lineChartLabels,
    metricsQuery.data?.series.filteredPassRate,
    metricsQuery.isFetched,
  ]);
  const frequencyChartData = useMemo(() => {
    const series = buildLineChartSeries(
      metricsQuery.data?.series.executionTrendMetrics,
      lineChartLabels,
      chartDates,
      'frequency',
    );
    if (series) return series;
    return metricsQuery.isFetched ? [] : null;
  }, [
    chartDates,
    lineChartLabels,
    metricsQuery.data?.series.executionTrendMetrics,
    metricsQuery.isFetched,
  ]);
  const durationChartData = useMemo(() => {
    const series = buildLineChartSeries(
      metricsQuery.data?.series.executionDurationAverage,
      lineChartLabels,
      chartDates,
      'duration',
    );
    if (series) return series;
    return metricsQuery.isFetched ? [] : null;
  }, [
    chartDates,
    lineChartLabels,
    metricsQuery.data?.series.executionDurationAverage,
    metricsQuery.isFetched,
  ]);
  const handleChartSelect = useCallback(
    (dataIndex: number) => {
      const day = chartDates[dataIndex];
      const nextSelection = resolveChartDaySelection(selectedStartDay, selectedEndDay, day);
      setSelectedStartDay(nextSelection.startDay);
      setSelectedEndDay(nextSelection.endDay);
    },
    [chartDates, selectedEndDay, selectedStartDay],
  );
  const showLineChartPoints = Boolean(
    selectedStartDay && selectedEndDay && selectedStartDay === selectedEndDay,
  );
  const showChartDateClear = Boolean(selectedStartDay ?? selectedEndDay);
  const handleChartDateClear = useCallback(() => {
    setSelectedStartDay(null);
    setSelectedEndDay(null);
  }, []);
  const passRateGuideLines = useMemo(
    () => buildThresholdGuideLines(thresholds.tests.passRate),
    [thresholds.tests.passRate],
  );
  const frequencyGuideLines = useMemo(
    () => buildThresholdGuideLines(thresholds.tests.frequency),
    [thresholds.tests.frequency],
  );
  const durationGuideLines = useMemo(
    () => buildThresholdGuideLines(thresholds.tests.avgDuration),
    [thresholds.tests.avgDuration],
  );

  useEffect(() => {
    setSuppressChartAnimation(true);
    const timer = window.setTimeout(() => {
      setSuppressChartAnimation(false);
    }, 0);
    return () => {
      window.clearTimeout(timer);
    };
  }, [chartTab]);

  return {
    title,
    testId,
    chartTab,
    setChartTab,
    suppressChartAnimation,
    range,
    setRange,
    selectedStartDay,
    setSelectedStartDay,
    selectedEndDay,
    setSelectedEndDay,
    isAllMode,
    pickerStartDay,
    pickerEndDay,
    handleStartDayChange,
    handleEndDayChange,
    testMetricCards,
    metricValueItems,
    chartHeight,
    showChartDateClear,
    handleChartDateClear,
    stackedChartData,
    passRateChartData,
    frequencyChartData,
    durationChartData,
    passRateGuideLines,
    frequencyGuideLines,
    durationGuideLines,
    handleChartSelect,
    showLineChartPoints,
    selectedTags,
    tagOptions,
    tagOptionsLoading,
    handleTagsChange: setSelectedTags,
    testExecutionFilters,
  };
};

export const useTestDetailPageViewModel = (
  params: TestDetailPageViewModelParams,
): TestDetailPageViewModel => useTestDetailPageViewModelInternal(params);
